// Imports ------------------------------------------------------------------->

import { ConfigService, RuntimeConfig } from '../../services/ConfigService'
import { inject } from '../../services/di'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export class Sample {
    gamepads: Sample.GamepadState[] = []
    wheelDeltaX: number = 0
    wheelDeltaY: number = 0
    keys: boolean[] = []
    dt: number = 0
    pointers: { [key: string]: Sample.PointerState } = {}
}

export namespace Sample {
    export const enum Button {
        LEFT = 1,
        MIDDLE = 4,
        RIGHT = 2,
    }

    export interface PointerState {
        x: number
        y: number
        dx: number
        dy: number
        pressure: number
        buttons: number
        kind: string
        owned: boolean
    }

    export interface GamepadState {
        id: string
        index: number
        timestamp: number
        axes: number[]
        buttons: {
            value: number
            pressed: boolean
        }[]
        connected: boolean
        mapping: string
    }
}

interface InternalPointerState extends Sample.PointerState {
    pollNumber: number
    deferredUp: boolean
}

export class InputSampler {
    // Used for `wheel` events where `event.deltaMode` is DOM_DELTA_LINE
    private static WHEEL_LINE_HEIGHT = 30

    // FIXME [FW-294] Used when `event.deltaMode` is DOM_DELTA_PAGE, we might
    // want to make it use the actual flyer page width.
    private static WHEEL_PAGE_HEIGHT = 800

    @inject config: ConfigService

    oninput: () => void
    ontrackinginput: () => void

    private _keys: boolean[] = []
    private _wheelDeltaX = 0
    private _wheelDeltaY = 0
    private _numGamepads = 0
    private _numKeys = 0
    private _activePointers: { [key: string]: InternalPointerState } = {}
    private _dirty = false
    private _ignoreNegativeMoves = false
    private _deferredUps: (MouseEvent | TouchEvent | PointerEvent)[] = []
    private _pollCount = 0
    private _lastT: number

    // Helper function to account for the differences between touch events.
    // Applies a function to the client coordinates of each touch involved
    // in the given.
    private static _forEachPointer(
        e: MouseEvent | TouchEvent | PointerEvent,
        fn: (
            clientX: number,
            clientY: number,
            pressure: number,
            buttons: number,
            id: number,
            kind: string
        ) => void
    ) {
        if ('onpointerdown' in window && e instanceof PointerEvent) {
            fn(
                e.clientX,
                e.clientY,
                e.pressure,
                e.buttons,
                e.pointerId,
                e.pointerType
            )
        } else if (e instanceof MouseEvent) {
            let buttons = e.buttons
            if (buttons === undefined) {
                if (e.which === 1) buttons = Sample.Button.LEFT
                else if (e.which === 2) buttons = Sample.Button.MIDDLE
                else if (e.which === 3) buttons = Sample.Button.RIGHT
                else buttons = 0
            }
            fn(e.clientX, e.clientY, 0.5, buttons, 0, 'mouse')
        } else if ('ontouchstart' in window && e instanceof TouchEvent) {
            for (let i = 0, n = e.changedTouches.length; i < n; ++i) {
                const t = e.changedTouches[i]
                fn(
                    t.clientX,
                    t.clientY,
                    t.force,
                    Sample.Button.LEFT,
                    t.identifier,
                    'touch'
                )
            }
        }
    }

    constructor(
        private _pointerElement: HTMLElement,
        private _keyboardElement: HTMLElement
    ) {
        this._pointerElement.tabIndex = -1
        this._ignoreNegativeMoves =
            !!navigator.userAgent.match(/iPad/) ||
            !!navigator.userAgent.match(/iPhone/)
    }

    attach() {
        this._keyboardElement.addEventListener('keydown', this._handleKeyDown)
        this._keyboardElement.addEventListener('keyup', this._handleKeyUp)
        this._pointerElement.addEventListener('blur', this._handleBlur)
        if (this.config.RUNTIME !== RuntimeConfig.PG) {
            this._pointerElement.addEventListener(
                'mousewheel',
                this._handleWheel
            )
            this._pointerElement.addEventListener('wheel', this._handleWheel)
        }

        const el = this._pointerElement
        if ('onpointerdown' in window) {
            el.addEventListener('pointerdown', this._handleDown)
            ;(window as any).addEventListener('pointermove', this._handleMove)
            ;(window as any).addEventListener('pointerup', this._handleUp)
            ;(window as any).addEventListener('pointercancel', this._handleUp)
        } else if ('ontouchstart' in window) {
            el.addEventListener('touchstart', this._handleDown)
            ;(window as any).addEventListener('touchmove', this._handleMove)
            ;(window as any).addEventListener('touchend', this._handleUp)
            ;(window as any).addEventListener('touchcancel', this._handleUp)
            el.addEventListener('mousedown', this._handleDown)
            ;(window as any).addEventListener('mousemove', this._handleMove)
            ;(window as any).addEventListener('mouseup', this._handleUp)
        } else {
            el.addEventListener('mousedown', this._handleDown)
            ;(window as any).addEventListener('mousemove', this._handleMove)
            ;(window as any).addEventListener('mouseup', this._handleUp)
        }
    }

    detach() {
        this._keyboardElement.removeEventListener(
            'keydown',
            this._handleKeyDown
        )
        this._keyboardElement.removeEventListener('keyup', this._handleKeyUp)
        this._pointerElement.removeEventListener('blur', this._handleBlur)
        if (this.config.RUNTIME !== RuntimeConfig.PG) {
            this._pointerElement.removeEventListener(
                'mousewheel',
                this._handleWheel
            )
            this._pointerElement.removeEventListener('wheel', this._handleWheel)
        }

        const el = this._pointerElement
        if ('onpointerdown' in window) {
            el.removeEventListener('pointerdown', this._handleDown)
            ;(window as any).removeEventListener(
                'pointermove',
                this._handleMove
            )
            ;(window as any).removeEventListener('pointerup', this._handleUp)
            ;(window as any).removeEventListener(
                'pointercancel',
                this._handleUp
            )
        } else if ('ontouchstart' in window) {
            el.removeEventListener('touchstart', this._handleDown)
            ;(window as any).removeEventListener('touchmove', this._handleMove)
            ;(window as any).removeEventListener('touchend', this._handleUp)
            ;(window as any).removeEventListener('touchcancel', this._handleUp)
            el.removeEventListener('mousedown', this._handleDown)
            ;(window as any).removeEventListener('mousemove', this._handleMove)
            ;(window as any).removeEventListener('mouseup', this._handleUp)
        } else {
            el.removeEventListener('mousedown', this._handleDown)
            ;(window as any).removeEventListener('mousemove', this._handleMove)
            ;(window as any).removeEventListener('mouseup', this._handleUp)
        }
    }

    get polling() {
        return this._numGamepads > 0 || this._numKeys > 0 || this._dirty
    }

    poll() {
        ++this._pollCount

        const sample = new Sample()
        const t = flipp.now()
        const dt = t - this._lastT
        this._lastT = t

        if (this._numGamepads === 0 && this._numKeys === 0 && !this._dirty)
            return null

        if (this._numGamepads > 0) {
            const gamepads = navigator.getGamepads
                ? navigator.getGamepads()
                : []
            for (let i = 0, n = gamepads.length; i < n; ++i) {
                const gamepad = gamepads[i]
                if (gamepad) {
                    sample.gamepads.push({
                        id: gamepad.id,
                        index: gamepad.index,
                        timestamp: gamepad.timestamp,
                        axes: gamepad.axes.slice(),
                        buttons: gamepad.buttons.map((button) => {
                            return {
                                value: button.value,
                                pressed: button.pressed,
                            }
                        }),
                        connected: gamepad.connected,
                        mapping: gamepad.mapping,
                    })
                } else {
                    sample.gamepads.push(gamepad as any)
                }
            }
        }

        sample.wheelDeltaX = this._wheelDeltaX
        sample.wheelDeltaY = this._wheelDeltaY
        sample.keys = this._keys.slice()
        sample.dt = dt

        for (const k in this._activePointers) {
            if (!this._activePointers.hasOwnProperty(k)) continue

            const pointer = this._activePointers[k]
            sample.pointers[k] = {
                x: pointer.x * flipp.devicePixelRatio,
                y: pointer.y * flipp.devicePixelRatio,
                dx: pointer.dx * flipp.devicePixelRatio,
                dy: pointer.dy * flipp.devicePixelRatio,
                buttons: pointer.buttons,
                pressure: pointer.pressure,
                kind: pointer.kind,
                owned: pointer.owned,
            }
            pointer.dx = 0
            pointer.dy = 0
        }

        this._wheelDeltaX = 0
        this._wheelDeltaY = 0
        this._dirty = false

        if (this._deferredUps.length > 0) {
            for (const e of this._deferredUps) this._handleUp(e)
            this._deferredUps = []
            this._dirty = true
        }

        return sample
    }

    private _handleUp = (e: MouseEvent | TouchEvent | PointerEvent) => {
        let foundActivePointer = false

        InputSampler._forEachPointer(
            e,
            (clientX, clientY, value, buttons, id) => {
                const pointer = this._activePointers[id]
                if (pointer && pointer.owned) {
                    if (pointer.pollNumber === this._pollCount) {
                        this._deferredUps.push(e)
                        pointer.deferredUp = true
                        return
                    }
                    foundActivePointer = true
                }
                delete this._activePointers[id]
            }
        )

        if (foundActivePointer) {
            e.preventDefault()
            this._dirty = true
            if (this.oninput) this.oninput()
        }
    }

    private _handleMove = (e: MouseEvent | TouchEvent | PointerEvent) => {
        let foundActivePointer = false
        const clientRect = this._pointerElement.getBoundingClientRect()
        InputSampler._forEachPointer(
            e,
            (clientX, clientY, pressure, buttons, id, kind) => {
                // On iOS, it looks like the only way to detect the swipe to navigate
                // backwards gesture is detecting moves past the left edge of the
                // window. If we don't detect these then the flyer moves while
                // swiping backward for navigation.
                if (this._ignoreNegativeMoves && clientX < 0) return

                const pointer = this._activePointers[id]
                if (!pointer) {
                    this._activePointers[id] = {
                        x: clientX - clientRect.left,
                        y: clientY - clientRect.top,
                        dx: 0,
                        dy: 0,
                        pressure: pressure,
                        buttons: buttons,
                        kind: kind,
                        owned: false,
                        pollNumber: this._pollCount,
                        deferredUp: false,
                    }
                    return
                }

                const target = e.target
                let t: HTMLElement
                if (target instanceof HTMLElement) {
                    t = target
                    while (t && t !== this._pointerElement) t = t.parentElement
                }
                if (pointer.owned || t === this._pointerElement)
                    foundActivePointer = true

                const x = clientX - clientRect.left
                const y = clientY - clientRect.top
                pointer.dx = x - pointer.x
                pointer.dy = y - pointer.y
                pointer.x = x
                pointer.y = y
                if (!pointer.deferredUp) pointer.buttons = buttons
                pointer.pressure = pressure
            }
        )

        if (foundActivePointer) {
            e.preventDefault()
            this._dirty = true
            if (this.ontrackinginput) this.ontrackinginput()
        }
    }

    private _handleDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
        e.preventDefault()

        // Add new pointers to the active pointer set. The first two pointers
        // to come down become the pointers used for drags and pinches. Additional
        // pointers are ignored until the first two come up, at which point they
        // may be promoted into `_fstPointer` or `_sndPointer`.
        const clientRect = this._pointerElement.getBoundingClientRect()
        InputSampler._forEachPointer(
            e,
            (clientX, clientY, p, buttons, id, k) => {
                this._activePointers[id] = {
                    x: clientX - clientRect.left,
                    y: clientY - clientRect.top,
                    dx: 0,
                    dy: 0,
                    pressure: p,
                    buttons: buttons,
                    kind: k,
                    owned: true,
                    pollNumber: this._pollCount,
                    deferredUp: false,
                }
            }
        )
        this._dirty = true
        if (this.oninput) this.oninput()
    }

    private _handleBlur = () => {
        this._keys = []
        this._numKeys = 0
    }

    private _handleKeyDown = (e: KeyboardEvent) => {
        if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return

        if (!this._keys[e.keyCode]) ++this._numKeys

        this._keys[e.keyCode] = true
        this._dirty = true
        if (this.oninput) this.oninput()
    }

    private _handleKeyUp = (e: KeyboardEvent) => {
        if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return

        if (this._keys[e.keyCode]) --this._numKeys

        delete this._keys[e.keyCode]
        this._dirty = true
    }

    private _handleWheel = (e: WheelEvent) => {
        e.preventDefault()

        if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
            this._wheelDeltaX += e.deltaX * InputSampler.WHEEL_LINE_HEIGHT
            this._wheelDeltaY += e.deltaY * InputSampler.WHEEL_LINE_HEIGHT
        } else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
            this._wheelDeltaX += e.deltaX * InputSampler.WHEEL_PAGE_HEIGHT
            this._wheelDeltaY += e.deltaY * InputSampler.WHEEL_PAGE_HEIGHT
        } else {
            this._wheelDeltaX += e.deltaX
            this._wheelDeltaY += e.deltaY
        }

        this._dirty = true
        if (this.ontrackinginput) this.ontrackinginput()
    }
}
